'use client'

import dynamic from 'next/dynamic'
import { C, Callout, Exercise, Figure, H, Lesson, M, MB, P, Quiz, Step } from '../_components/lesson'
import * as EX from './exercises/language'
import * as DX from './exercises/deepen'

const BigramExplorer = dynamic(() => import('../_components/widgets/LanguageWidgets').then(m => m.BigramExplorer), { ssr: false })

export default function BigramModel() {
  return (
    <Lesson>
      <Step>
        <H>A language model in a table</H>
        <P>
          A language model predicts the next token from the ones before it: <M>{'P(\\text{next} \\mid \\text{previous})'}</M>.
          The simplest possible version only looks at <strong>one</strong> previous token. Take ~600 first names, and for every
          letter, count which letter comes next. That table of counts <em>is</em> the model — a <strong>bigram</strong> model
          (bi = two: pairs of letters).
        </P>
        <P>We add a special “.” token for the start and end of a name, so the model also learns how names begin and end.</P>
        <Figure n="8.2" wide caption="Every cell counts how often the column letter follows the row letter (darker = more). Click a row to see the next-letter probabilities. Then generate names by repeatedly sampling from the row of the letter you just produced.">
          <BigramExplorer />
        </Figure>
      </Step>

      <Step gate="q-names">
        <H>How good is it?</H>
        <P>
          Some generated names are plausible, many are nonsense. To <em>measure</em> it, use cross-entropy (Module 5): go through
          every letter pair in the real names, and average the surprise <M>{'-\\ln P(\\text{next} \\mid \\text{prev})'}</M>.
        </P>
        <MB>{'\\pink{L} = -\\frac{1}{N}\\sum_{\\text{pairs}} \\ln \\violet{P}(c_{i+1} \\mid c_i)'}</MB>
        <P>
          Random guessing over 27 options scores <M>{'\\ln 27 \\approx 3.30'}</M>. The bigram table gets about 2.45 — better,
          but not great, because one letter of context isn’t much to go on.
        </P>
        <Quiz
          id="q-names"
          question="What does the ‘smoothing’ slider do, and why is it needed?"
          options={[
            { label: 'Adds a fake count to every pair, so no pair has probability exactly 0 (which would make the loss infinite).', correct: true },
            { label: 'Makes generated names longer.', why: 'It slightly evens out the probabilities, but its real job is preventing zeros.' },
            { label: 'Removes rare names from the data.', why: 'It changes the counts, not the dataset.' },
          ]}
          explain="If a valid name contained a pair never seen in training, P = 0 and −ln(0) = ∞. Adding a small count everywhere avoids that. Neural networks get this for free: softmax never outputs exactly zero."
        />
      </Step>

      <Step gate="bigram">
        <H>Build it</H>
        <Exercise
          id="bigram"
          title="Count, normalise, evaluate, sample"
          {...EX.bigram}
          hints={[
            <>
              For counting: <C>{'const cs = [".", ...name.split(""), "."]'}</C>, then for each i, increment{' '}
              <C>N[idx(cs[i])][idx(cs[i + 1])]</C>.
            </>,
            <>
              For probabilities: for each row, <C>total = vec.sum(row) + row.length</C>, then <C>(c + 1) / total</C>.
            </>,
            <>For the loss, walk the same pairs and add up <C>-Math.log(P[a][b])</C>; divide by how many pairs there were.</>,
          ]}
        />
        <Callout kind="why">
          Here’s the thing: you could also get this exact table by training a tiny neural network (27 inputs → 27 outputs, no
          hidden layer) with gradient descent on the same loss. Counting and training arrive at the same model. The neural
          version just generalises much better once you give it more context — next lessons.
        </Callout>
      </Step>

      <Step gate={['sparsity', 'q-sparse']}>
        <H>Why not count longer contexts?</H>
        <P>
          One letter of context is clearly too little. So why not count what follows each pair, or triple, of letters? Try it,
          and measure how often a new name contains a context the table has never seen.
        </P>
        <Exercise
          id="sparsity"
          title="contexts and unseenShare"
          {...DX.sparsity}
          hints={[
            <>
              <C>{'const s = ".".repeat(n) + name + "."'}</C>, then for each <C>i</C> from <C>n</C> to the end, push{' '}
              <C>{'s.slice(i - n, i)'}</C>.
            </>,
            <>
              Put the training contexts in a Set, and count the held-out contexts that aren’t in it.
            </>,
          ]}
        />
        <Quiz
          id="q-sparse"
          question="The table for 4 letters of context has over half a million rows, but only a few thousand letters of training text. What goes wrong?"
          options={[
            { label: 'Most rows are never filled, so the model knows nothing about contexts it hasn’t seen exactly', correct: true },
            { label: 'Nothing — more context is always better', why: 'More context needs exponentially more data to fill the table. This is the curse of dimensionality again.' },
            { label: 'The table becomes too slow to read', why: 'Looking up a row is fast. The problem is that most rows are empty.' },
          ]}
          explain="A table can’t see that “mar” and “mor” are similar. A neural network with embeddings can — which is exactly why the neural language model is coming up."
        />
      </Step>
    </Lesson>
  )
}
